import { useEffect, type ReactNode } from 'react';
import { COLOR_PALETTE } from '../lib/palette';
import type { Tag } from '../types';

/** Kit mínimo compartilhado pelas telas — sem dependência de UI externa. */

export function Button({
  children,
  variant = 'default',
  type = 'button',
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'default' | 'danger' | 'ghost';
}) {
  const base =
    'inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-[13px] font-medium transition disabled:opacity-50 disabled:cursor-not-allowed';
  const styles: Record<string, string> = {
    default: 'border border-slate-300 bg-white text-slate-700 hover:border-slate-400',
    danger: 'border border-red-200 bg-red-50 text-red-700 hover:bg-red-100',
    ghost: 'text-slate-600 hover:bg-slate-100',
    primary: 'text-white',
  };

  return (
    <button
      type={type}
      className={`${base} ${styles[variant]} ${props.className ?? ''}`}
      style={
        variant === 'primary'
          ? {
              backgroundColor: 'var(--crm-primary)',
              color: 'var(--crm-primary-foreground)',
              ...props.style,
            }
          : props.style
      }
      {...props}
    >
      {children}
    </button>
  );
}

export function Field({
  label,
  children,
  hint,
}: {
  label: string;
  children: ReactNode;
  hint?: string;
}) {
  return (
    <label className="block text-sm">
      <span className="font-medium text-slate-700">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-xs text-slate-400">{hint}</span>}
    </label>
  );
}

export const inputClass =
  'mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-[13px] outline-none focus:border-slate-500 disabled:bg-slate-50';

/** Campo de largura própria (barras de filtro, linhas de formulário): sem `w-full` nem margem. */
export const compactInputClass =
  'rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-[13px] outline-none focus:border-slate-500 disabled:bg-slate-50';

const AVATAR_COLORS = [
  '#7c3aed',
  '#0ea5e9',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#ec4899',
  '#14b8a6',
  '#6366f1',
];

/** Foto ou bolinha com a inicial, na cor fixa do nome (como no DataCrazy). */
export function Avatar({
  name,
  src,
  size = 'md',
}: {
  name: string;
  src?: string | null;
  size?: 'xs' | 'sm' | 'md' | 'lg';
}) {
  const box = {
    xs: 'h-4 w-4 text-[9px]',
    sm: 'h-6 w-6 text-[11px]',
    md: 'h-8 w-8 text-sm',
    lg: 'h-12 w-12 text-lg',
  }[size];
  if (src)
    return <img src={src} alt="" className={`${box} shrink-0 rounded-full object-cover`} />;
  const color =
    AVATAR_COLORS[
      [...name].reduce((sum, char) => sum + char.charCodeAt(0), 0) % AVATAR_COLORS.length
    ];
  return (
    <span
      className={`${box} flex shrink-0 items-center justify-center rounded-full font-semibold text-white`}
      style={{ backgroundColor: color }}
      aria-hidden
    >
      {name.trim().charAt(0).toUpperCase() || '?'}
    </span>
  );
}

/** Chip Pessoa (azul) / Empresa (roxo). */
export function KindBadge({ kind }: { kind: 'PERSON' | 'COMPANY' }) {
  return (
    <span
      className={`shrink-0 rounded border px-1.5 text-[10px] font-medium leading-4 ${
        kind === 'COMPANY'
          ? 'border-violet-300/60 text-violet-600'
          : 'border-sky-300/60 text-sky-600'
      }`}
    >
      {kind === 'COMPANY' ? 'Empresa' : 'Pessoa'}
    </span>
  );
}

export function Modal({
  title,
  onClose,
  children,
  wide = false,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
}) {
  // Esc fecha — o modal é usado em fluxo rápido de cadastro.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/40 p-4 pt-16"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <div
        className={`w-full ${wide ? 'max-w-3xl' : 'max-w-lg'} rounded-xl border border-slate-200 bg-white shadow-xl`}
      >
        <header className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-2 text-lg leading-none text-slate-400 hover:bg-slate-100"
            aria-label="Fechar"
          >
            ×
          </button>
        </header>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

export function TagPill({ tag, onRemove }: { tag: Tag; onRemove?: () => void }) {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium"
      style={{
        backgroundColor: `${tag.color ?? '#64748b'}1a`,
        color: tag.color ?? '#475569',
      }}
    >
      {tag.name}
      {onRemove && (
        <button type="button" onClick={onRemove} className="opacity-60 hover:opacity-100">
          ×
        </button>
      )}
    </span>
  );
}

export function StatusBadge({ status }: { status: 'in_process' | 'won' | 'lost' }) {
  const labels = { in_process: 'Em andamento', won: 'Ganho', lost: 'Perdido' };
  const styles = {
    in_process: 'bg-sky-50 text-sky-700',
    won: 'bg-emerald-50 text-emerald-700',
    lost: 'bg-red-50 text-red-700',
  };
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${styles[status]}`}>
      {labels[status]}
    </span>
  );
}

export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="rounded-xl border border-dashed border-slate-300 p-8 text-center">
      <p className="text-sm font-medium text-slate-600">{title}</p>
      {hint && <p className="mt-1 text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

export function Loading({ label = 'Carregando…' }: { label?: string }) {
  return <p className="p-6 text-sm text-slate-500">{label}</p>;
}

export function ErrorNote({ children }: { children: ReactNode }) {
  return <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{children}</p>;
}

export function ColorPalette({
  value,
  onChange,
  colors = COLOR_PALETTE,
  allowEmpty = false,
}: {
  value: string | null;
  onChange: (color: string | null) => void;
  colors?: string[];
  allowEmpty?: boolean;
}) {
  return (
    <div className="mt-1 flex flex-wrap gap-1.5" role="radiogroup" aria-label="Cor">
      {allowEmpty && (
        <button
          type="button"
          role="radio"
          aria-checked={value === null}
          aria-label="Sem cor"
          onClick={() => onChange(null)}
          className={`h-6 w-6 rounded-full border border-dashed border-slate-300 text-[10px] text-slate-400 ${
            value === null ? 'ring-2 ring-slate-900 ring-offset-1' : ''
          }`}
        >
          ∅
        </button>
      )}
      {colors.map((color) => (
        <button
          key={color}
          type="button"
          role="radio"
          aria-checked={value?.toUpperCase() === color}
          aria-label={color}
          onClick={() => onChange(color)}
          className={`h-6 w-6 rounded-full border border-slate-200 ${
            value?.toUpperCase() === color ? 'ring-2 ring-slate-900 ring-offset-1' : ''
          }`}
          style={{ backgroundColor: color }}
        />
      ))}
    </div>
  );
}

export function Toggle({
  checked,
  onChange,
  label,
  hint,
  disabled,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  hint?: string;
  disabled?: boolean;
}) {
  return (
    <label className={`flex items-start gap-3 text-sm ${disabled ? 'opacity-50' : ''}`}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`mt-0.5 h-5 w-9 shrink-0 rounded-full p-0.5 transition ${
          checked ? '' : 'bg-slate-300'
        }`}
        style={checked ? { backgroundColor: 'var(--crm-primary)' } : undefined}
      >
        <span
          className={`block h-4 w-4 rounded-full bg-white transition ${checked ? 'translate-x-4' : ''}`}
        />
      </button>
      <span>
        <span className="font-medium text-slate-700">{label}</span>
        {hint && <span className="block text-xs text-slate-400">{hint}</span>}
      </span>
    </label>
  );
}

/**
 * Confirmação de ação de efeito (em massa, irreversível ou que dispara algo).
 * Decisão do plano DC1: nada disso acontece a um clique.
 */
export function ConfirmDialog({
  title,
  message,
  confirmLabel = 'Confirmar',
  danger = false,
  busy = false,
  error,
  onConfirm,
  onClose,
}: {
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  busy?: boolean;
  error?: string | null;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Modal title={title} onClose={onClose}>
      <div className="space-y-4 text-sm text-slate-600">
        <div>{message}</div>
        {error && <ErrorNote>{error}</ErrorNote>}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm} disabled={busy}>
            {busy ? 'Aguarde…' : confirmLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
}: {
  tabs: Array<[T, string]>;
  value: T;
  onChange: (tab: T) => void;
}) {
  return (
    <div className="flex gap-1 border-b border-slate-200" role="tablist">
      {tabs.map(([key, label]) => (
        <button
          key={key}
          type="button"
          role="tab"
          aria-selected={value === key}
          onClick={() => onChange(key)}
          className={`-mb-px border-b-2 px-3 py-2 text-sm ${
            value === key
              ? 'border-slate-900 font-medium text-slate-900'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}
